import React, { useState } from 'react';
import { Fab, Tooltip, Badge, Box } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useLocation } from 'react-router-dom';
import { useAITips } from '../../Context/AITipsContext';
import { getTipsForPage, hasTipsForPage } from '../../Utils/aiTipsConfig';
import AITipCard from '../AITipCard/AITipCard';
import './AITipButton.css';

const AITipButton = () => {
  const location = useLocation();
  const { showTips, toggleTips, closeTips, hasSeenTips } = useAITips();
  const [isHovered, setIsHovered] = useState(false);

  const currentPath = location.pathname;
  const tips = getTipsForPage(currentPath);
  const hasUnseenTips = !hasSeenTips[currentPath] && hasTipsForPage(currentPath);

  // Don't show the button if there are no tips for this page
  if (!hasTipsForPage(currentPath)) {
    return null;
  }

  return (
    <>
      <Tooltip
        title="Get AI tips for this page"
        placement="left"
        arrow
      >
        <Box
          sx={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            zIndex: 9998
          }}
        >
          <Badge
            badgeContent={hasUnseenTips ? '!' : 0}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: '18px',
                minWidth: '18px',
                borderRadius: '50%',
                fontWeight: 'bold',
                animation: hasUnseenTips ? 'pulse 2s infinite' : 'none'
              }
            }}
          >
            <Fab
              color="primary"
              aria-label="AI tips"
              onClick={toggleTips}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              sx={{
                backgroundColor: '#002244',
                width: '48px',
                height: '48px',
                boxShadow: '0 3px 15px rgba(0, 34, 68, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0D3B66',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0, 34, 68, 0.5)',
                },
                animation: hasUnseenTips ? 'buttonPulse 2s infinite' : 'none',
                '@keyframes buttonPulse': {
                  '0%': {
                    boxShadow: '0 3px 15px rgba(0, 34, 68, 0.4)'
                  },
                  '50%': {
                    boxShadow: '0 3px 22px rgba(255, 215, 0, 0.6)'
                  },
                  '100%': {
                    boxShadow: '0 3px 15px rgba(0, 34, 68, 0.4)'
                  }
                }
              }}
            >
              <LightbulbIcon
                sx={{
                  fontSize: '24px',
                  color: isHovered || showTips ? '#FFD700' : '#FFFFFF',
                  transition: 'color 0.3s ease'
                }}
              />
            </Fab>
          </Badge>
        </Box>
      </Tooltip>

      {/* Show the tip card when tips are active */}
      {showTips && tips.length > 0 && (
        <AITipCard
          tips={tips}
          onClose={closeTips}
          position="bottom-right"
        />
      )}
    </>
  );
};

export default AITipButton;
